import { Badge } from './ui/badge';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

interface Option { value: string; label: string }
interface Props {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
}

/** Multi-select as toggle chips with badges, micro-animations, and full keyboard accessibility. */
export function Chips({ label, options, selected, onChange }: Props) {
  const all = selected.length === options.length;
  const none = selected.length === 0;

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-txt/90">
            {label}
          </label>
          {!all && !none && (
            <Badge variant="gold" className="px-1.5 py-0 text-[9.5px]">
              {selected.length}/{options.length}
            </Badge>
          )}
        </div>
        <button
          type="button"
          className="text-[11px] font-semibold text-dim hover:text-gold2 transition-colors cursor-pointer tracking-wider"
          onClick={() => onChange(all ? [] : options.map((o) => o.value))}
        >
          {all ? t.none : t.all}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              className={cn(
                'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer select-none border',
                on
                  ? 'bg-gold text-ink border-gold font-semibold shadow-[0_2px_10px_rgba(240,164,49,0.25)] hover:bg-gold2'
                  : 'bg-glass-2 border-hair text-txt/70 hover:bg-glass-hover hover:border-hair2 hover:text-txt'
              )}
              aria-pressed={on}
              onClick={() => toggle(o.value)}
              title={o.label}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
